import { useState, type FormEvent } from 'react'
import { useAuth } from '../../app/authContext'
import { Button } from '../../components/ui/Button'
import { ApiError, NetworkError } from '../../api/errors'
import { useQueuedCount } from '../../outbox/useOutbox'
import { faNumber } from '../../lib/faNumbers'
import styles from './LoginScreen.module.css'

export function LoginScreen() {
  const { signIn, deviceId, sessionExpired } = useAuth()
  const queuedCount = useQueuedCount()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()

    if (submitting) {
      return
    }

    setError(null)
    setSubmitting(true)

    try {
      await signIn(username.trim(), password)
    } catch (cause) {
      if (cause instanceof NetworkError) {
        // Login is the one operation that cannot be queued — it is what produces
        // the token every queued operation will be sent with.
        setError('ارتباط با سرور برقرار نشد. اتصال شبکه را بررسی کنید.')
      } else if (cause instanceof ApiError) {
        setError(cause.message)
      } else {
        setError('ورود ناموفق بود. دوباره تلاش کنید.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className={styles.screen} onSubmit={handleSubmit}>
      <div className={styles.brand}>
        <div className={styles.mark} aria-hidden="true">
          پ
        </div>
        <div className={styles.name}>پالت اسکنر انبار</div>
        <div className={styles.version}>v1.0 · WAREHOUSE PWA</div>
      </div>

      {/*
        A dead session must not read as a failed login. The queued count is shown
        because the outbox survives re-login untouched, and the operator needs to
        know their scans are still safe on the device.
      */}
      {sessionExpired && (
        <div className={styles.notice} role="status">
          نشست شما منقضی شده است. دوباره وارد شوید.
          {queuedCount > 0 && <> {faNumber(queuedCount)} اسکن روی دستگاه محفوظ است و پس از ورود ارسال می‌شود.</>}
        </div>
      )}

      {error !== null && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}

      <div className={styles.fields}>
        <label className={styles.label} htmlFor="username">
          نام کاربری اپراتور
        </label>
        <input
          id="username"
          className={styles.input}
          name="username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
        />

        <label className={`${styles.label} ${styles.labelSpaced}`} htmlFor="password">
          گذرواژه
        </label>
        <input
          id="password"
          className={`${styles.input} ${styles.password}`}
          name="password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
      </div>

      <Button type="submit" disabled={submitting || username.trim() === '' || password === ''}>
        ورود به سامانه
      </Button>

      <div className={styles.footer}>
        <span>دستگاه: {deviceId}</span>
        <span className={styles.endpoint}>wms.local/api/v1</span>
      </div>
    </form>
  )
}
